import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';

export interface ClerkProduct {
  id: string | number;
  name: string;
  price?: number;
  list_price?: number;
  image?: string;
  url?: string;
  [key: string]: any;
}


export interface ClerkCategory {
  id: string | number;
  name: string;
  url?: string;
  parent?: string | number;
  children?: any[];
  subcategories?: any[];
  [key: string]: any;
}

export interface ClerkPredictiveResponse {
  status: string;
  result: (string | number)[];
  hits: number;
  product_data: ClerkProduct[];
  query: string;
}

export interface ClerkCategoryResponse {
  status: string;
  result: (string | number)[];
  categories: ClerkCategory[];
}

export interface ClerkSuggestionsResponse {
  status: string;
  result: string[];
}

export interface ClerkFacetUnit {
  type: 'unit';
  name: string;
  value: number | string;
  count: number;
}

export interface ClerkFacetRange {
  type: 'range';
  name: string;
  min: number;
  max: number;
  count: number;
}

export type ClerkFacets = {
  [key: string]: (ClerkFacetUnit | ClerkFacetRange)[];
};

export interface ClerkSaleProduct {
  id: string | number;
  price?: number;
  quantity?: number;
}

export interface ClerkLogSaleRequest {
  sale: string | number;
  products: (string | number | ClerkSaleProduct)[];
  customer?: string | number;
}

export interface ClerkSearchResponse {
  status: string;
  result: (string | number)[];
  count: number;
  hits: number;
  product_data: ClerkProduct[];
  query: string;
  facets?: ClerkFacets;
  content_data?: { headline?: string };
}

@Injectable({
  providedIn: 'root'
})
export class ClerkSearchService {
  private readonly baseUrl = 'https://api.clerk.io/v2';
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private readonly visitorStorageKey = 'clerk_visitor_id';

  protected defaultAttributes = JSON.stringify(['id', 'name', 'image', 'list_price', 'url']);

  protected email: string | undefined;

  constructor() {
    this.authService.getCurrentUser().subscribe(user => {
      this.email = user?.email || undefined;
    });
  }

  getVisitorId(): string {
    let visitorId = localStorage.getItem(this.visitorStorageKey);
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      localStorage.setItem(this.visitorStorageKey, visitorId);
    }
    return visitorId;
  }

  private baseParams(apiKey: string): { [key: string]: string } {
    const params: { [key: string]: string } = { key: apiKey, visitor: this.getVisitorId() };
    if (this.email) {
      params['email'] = this.email;
    }
    return params;
  }

  predictive(query: string, apiKey: string, limit: number = 5): Observable<ClerkPredictiveResponse> {
    return this.http.get<ClerkPredictiveResponse>(`${this.baseUrl}/search/predictive`, {
      params: { ...this.baseParams(apiKey), query, limit: String(limit), attributes: this.defaultAttributes, labels: JSON.stringify(['Search - Predictive']) }
    });
  }

  categories(query: string, apiKey: string, limit: number = 6): Observable<ClerkCategoryResponse> {
    return this.http.get<ClerkCategoryResponse>(`${this.baseUrl}/search/categories`, {
      params: { ...this.baseParams(apiKey), query, limit: String(limit), labels: JSON.stringify(['Search - Categories']) }
    });
  }

  suggestions(query: string, apiKey: string, limit: number = 3): Observable<ClerkSuggestionsResponse> {
    return this.http.get<ClerkSuggestionsResponse>(`${this.baseUrl}/search/suggestions`, {
      params: { ...this.baseParams(apiKey), query, limit: String(limit), labels: JSON.stringify(['Search - Suggestions']) }
    });
  }

  /** Associate the current visitor with their email. Call this once the user's email is known (e.g. after login). */
  logEmail(apiKey: string): Observable<{ status: string }> {
    if (!this.email) {
      throw new Error('ClerkSearchService.logEmail: email is not set');
    }
    return this.http.get<{ status: string }>(`${this.baseUrl}/log/email`, {
      params: { key: apiKey, visitor: this.getVisitorId(), email: this.email }
    });
  }

  /** Log a product click. Only visitor is sent — Clerk's log/click does not accept email. */
  logClick(apiKey: string, productId: string | number, label: string, api: string): Observable<{ status: string }> {
    return this.http.get<{ status: string }>(`${this.baseUrl}/log/click`, {
      params: { key: apiKey, visitor: this.getVisitorId(), product: String(productId), api, labels: JSON.stringify([label]) }
    });
  }

  logSale(apiKey: string, data: ClerkLogSaleRequest): Observable<{ status: string }> {
    const body: { [key: string]: any } = {
      key: apiKey,
      visitor: this.getVisitorId(),
      sale: data.sale,
      products: data.products
    };
    if (this.email) {
      body['email'] = this.email;
    }
    if (data.customer != null) {
      body['customer'] = data.customer;
    }
    return this.http.post<{ status: string }>(`${this.baseUrl}/log/sale`, body);
  }

  search(
    query: string,
    apiKey: string,
    limit: number = 32,
    offset: number = 0,
    facets: string[] = [],
    filter?: string
  ): Observable<ClerkSearchResponse> {
    const params: { [key: string]: string } = {
      ...this.baseParams(apiKey),
      query,
      limit: String(limit),
      offset: String(offset),
      attributes: this.defaultAttributes,
      labels: JSON.stringify(['Search - Search page'])
    };
    if (facets.length > 0) {
      params['facets'] = JSON.stringify(facets);
    }
    if (filter) {
      params['filter'] = filter;
    }

    return this.http.get<ClerkSearchResponse>(`${this.baseUrl}/search/search`, { params });
  }
}
